import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import type { CliActionResult, DriftFinding, DriftFindingDetail } from '@simpleacs/shared';
import { api } from '../lib/api';
import { useTasks } from '../lib/tasks';
import { formatTimestamp } from '../lib/format';
import { useSession } from '../lib/session';
import { Button, ErrorNotice, Pill, Spinner } from '../components/ui';

/**
 * One drift finding, opened to decide on.
 *
 * The dialog shows both sides — what SimpleACS holds and what the chassis
 * answered — and then the two ways to close the finding. Apply is offered only
 * where the nightly check recorded a fix, and it runs in the house shape for
 * chassis writes: a dry run first, shown back with its warnings, then the real
 * run with the chassis transcript on screen. Dismiss asks for a reason and
 * keeps it on the row; the same difference will not be re-reported, a changed
 * one will.
 */
export function DriftFindingDialog({
  findingId,
  onClose,
}: {
  findingId: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const tasks = useTasks();
  const { can } = useSession();
  const [error, setError] = useState<string | null>(null);
  /** view → (dismiss | confirm); the dialog never stacks two flows. */
  const [mode, setMode] = useState<'view' | 'dismiss' | 'confirm'>('view');
  const [reason, setReason] = useState('');
  const [plan, setPlan] = useState<CliActionResult | null>(null);

  const query = useQuery({
    queryKey: ['drift', 'finding', findingId],
    queryFn: () => api.get<DriftFindingDetail>(`/drift/findings/${encodeURIComponent(findingId)}`),
  });
  const detail = query.data ?? null;
  const finding = detail?.finding ?? null;

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['drift'] });

  const dryRun = useMutation({
    mutationFn: () =>
      api.post<CliActionResult>(`/drift/findings/${encodeURIComponent(findingId)}/apply`, {
        dryRun: true,
      }),
    onSuccess: (r) => {
      setError(null);
      setPlan(r);
      setMode('confirm');
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Could not build the preview'),
  });

  /**
   * The real apply is a full chassis session (the same recorded fix as a
   * Resync), so it CLOSES the dialog and runs in the background tray rather than
   * holding the operator on a spinner and a transcript. Drift is refreshed on
   * either outcome — a rejection closes the finding as fix_failed.
   */
  const startApply = () => {
    const subject = finding?.subject ?? 'Finding';
    onClose();
    tasks.start<{ finding: DriftFinding; result: CliActionResult }>({
      label: `Apply fix — ${subject}`,
      run: async () => {
        try {
          return await api.post<{ finding: DriftFinding; result: CliActionResult }>(
            `/drift/findings/${encodeURIComponent(findingId)}/apply`,
            { dryRun: false },
          );
        } finally {
          await refresh();
        }
      },
      done: () => `${subject} fixed — the recorded commands ran on the chassis.`,
    });
  };

  /**
   * Dismiss is a record write, not a chassis session, but it goes the same way
   * as Apply: the dialog closes on the click and the tray reports the outcome,
   * so a refused dismiss is not lost with the dialog.
   */
  const startDismiss = () => {
    const subject = finding?.subject ?? 'Finding';
    const why = reason.trim();
    onClose();
    tasks.start({
      label: `Dismiss finding — ${subject}`,
      run: () =>
        api.post(`/drift/findings/${encodeURIComponent(findingId)}/dismiss`, { reason: why }),
      done: () =>
        `${subject} dismissed. The reason stays on the row, and the same difference will not be re-reported.`,
      invalidate: [['drift']],
    });
  };

  const open = finding?.status === 'open';
  const onuLink = finding?.onuId ?? finding?.deviceId ?? null;
  const missingSet = new Set(detail?.lines?.missing ?? []);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-8">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-2xl rounded-xl border border-line bg-surface shadow-2xl">
        <header className="flex items-start justify-between gap-4 border-b border-line px-5 py-4">
          <div className="min-w-0">
            <h2 className="flex flex-wrap items-center gap-2 text-sm font-semibold text-ink">
              <span className="font-mono">{finding?.subject ?? '…'}</span>
              {finding && (
                <Pill state
                  tone={
                    finding.status === 'open'
                      ? 'warn'
                      : finding.status === 'fixed'
                        ? 'success'
                        : finding.status === 'fix_failed'
                          ? 'danger'
                          : 'neutral'
                  }
                >
                  {finding.status.replace('_', ' ')}
                </Pill>
              )}
            </h2>
            {finding && (
              <p className="mt-0.5 text-xs text-muted">
                {finding.summary} · {finding.oltName ?? 'OLT gone'} · found{' '}
                {formatTimestamp(finding.createdAt)}
              </p>
            )}
          </div>
          {onuLink && (
            <Link
              to={`/onus/${encodeURIComponent(onuLink)}`}
              className="inline-flex shrink-0 items-center gap-1.5 text-xs text-primary hover:underline"
            >
              Open the ONU <ExternalLink className="size-3.5" />
            </Link>
          )}
        </header>

        <div className="max-h-[55vh] space-y-4 overflow-y-auto p-5">
          {error && <ErrorNotice error={new Error(error)} />}
          {query.isLoading && (
            <div className="flex justify-center p-8">
              <Spinner />
            </div>
          )}
          {query.error != null && <ErrorNotice error={query.error} />}

          {detail && mode !== 'confirm' && (
            <>
              {detail.lines && (
                <>
                  <section>
                    <h3 className="text-xs uppercase tracking-wide text-faint">
                      What SimpleACS holds — missing lines marked
                    </h3>
                    <pre className="mt-1.5 overflow-x-auto rounded-lg border border-line bg-surface-2 p-3 font-mono text-xs leading-relaxed">
                      {detail.lines.expected.map((line) => (
                        <div
                          key={line}
                          className={missingSet.has(line) ? 'text-danger' : 'text-ink'}
                        >
                          {missingSet.has(line) ? '− ' : '  '}
                          {line}
                        </div>
                      ))}
                    </pre>
                  </section>
                  <section>
                    <h3 className="text-xs uppercase tracking-wide text-faint">
                      What the ONU answered at the last check
                    </h3>
                    {detail.lines.chassis === null ? (
                      <p className="mt-1 text-xs text-muted">
                        This finding was recorded before the chassis's own lines were kept —
                        the next nightly pass captures them.
                      </p>
                    ) : detail.lines.chassis.length === 0 ? (
                      <p className="mt-1 text-xs text-muted">
                        The ONU answered with no vlan port lines at all.
                      </p>
                    ) : (
                      <pre className="mt-1.5 overflow-x-auto rounded-lg border border-line bg-surface-2 p-3 font-mono text-xs leading-relaxed text-ink">
                        {detail.lines.chassis.join('\n')}
                      </pre>
                    )}
                  </section>
                </>
              )}

              {detail.changes && detail.changes.length > 0 && (
                <section>
                  <h3 className="text-xs uppercase tracking-wide text-faint">
                    Differences — saved intent against the ONT
                  </h3>
                  <ul className="mt-1.5 space-y-1 rounded-lg border border-line bg-surface-2 p-3 font-mono text-xs leading-relaxed text-ink">
                    {detail.changes.map((change) => (
                      <li key={change}>{change}</li>
                    ))}
                  </ul>
                </section>
              )}

              {detail.fix.fixable ? (
                <section>
                  <h3 className="text-xs uppercase tracking-wide text-faint">
                    The fix the nightly check recorded
                  </h3>
                  <pre className="mt-1.5 overflow-x-auto rounded-lg border border-line bg-surface-2 p-3 font-mono text-xs leading-relaxed text-muted">
                    {detail.fix.commands.join('\n')}
                  </pre>
                </section>
              ) : (
                open && (
                  <section className="rounded-lg border border-line bg-surface-2 px-4 py-3">
                    <p className="text-xs text-muted">
                      <span className="font-medium text-ink">Review-only. </span>
                      {detail.fix.reviewOnly}
                    </p>
                  </section>
                )
              )}

              {detail.dismissed && (
                <section>
                  <h3 className="text-xs uppercase tracking-wide text-faint">Dismissed</h3>
                  <p className="mt-1 text-xs text-muted">
                    {formatTimestamp(detail.dismissed.at)} by {detail.dismissed.by}
                    {detail.dismissed.reason ? ` — “${detail.dismissed.reason}”` : ''}
                  </p>
                </section>
              )}
              {detail.applied && (
                <section>
                  <h3 className="text-xs uppercase tracking-wide text-faint">Applied</h3>
                  <p className="mt-1 text-xs text-muted">
                    {formatTimestamp(detail.applied.at)} by {detail.applied.by}
                  </p>
                  {detail.applied.rejected && detail.applied.rejected.length > 0 && (
                    <pre className="mt-1.5 overflow-x-auto rounded-lg border border-danger/40 bg-surface-2 p-3 font-mono text-xs text-danger">
                      {detail.applied.rejected.join('\n')}
                    </pre>
                  )}
                </section>
              )}

              {mode === 'dismiss' && (
                <section>
                  <h3 className="text-xs uppercase tracking-wide text-faint">
                    Why is this difference acceptable?
                  </h3>
                  <textarea
                    className="input mt-1.5 h-20 w-full resize-none"
                    value={reason}
                    autoFocus
                    maxLength={500}
                    placeholder="e.g. VLAN changed on site by the field team — the saved intent is the stale side."
                    onChange={(e) => setReason(e.target.value)}
                  />
                  <p className="mt-1 text-[11px] text-faint">
                    Kept on the finding, beside the evidence. The nightly check will not re-report
                    this exact difference; if it changes, a new finding opens.
                  </p>
                </section>
              )}
            </>
          )}

          {mode === 'confirm' && plan && detail && (
            <>
              <section className="rounded-lg border border-warn/40 bg-warn-soft px-4 py-3">
                <p className="text-xs text-ink">
                  About to write to <span className="font-medium">{finding?.oltName}</span>: re-apply
                  the recorded fix for <span className="font-mono">{finding?.subject}</span> —{' '}
                  {plan.commands.length} command{plan.commands.length === 1 ? '' : 's'}, exactly as
                  the nightly check rendered them.
                </p>
              </section>
              <section>
                <h3 className="text-xs uppercase tracking-wide text-faint">Commands</h3>
                <pre className="mt-1.5 overflow-x-auto rounded-lg border border-line bg-surface-2 p-3 font-mono text-xs leading-relaxed text-ink">
                  {plan.commands.join('\n')}
                </pre>
              </section>
              {plan.warnings.map((w) => (
                <p key={w} className="text-xs text-warn">
                  {w}
                </p>
              ))}
            </>
          )}

        </div>

        <footer className="flex flex-wrap items-center justify-between gap-2 border-t border-line px-5 py-3">
          <p className="text-[11px] text-faint">
            {mode === 'confirm'
              ? 'The commands run as recorded — nothing is composed here. Apply closes this and runs on the chassis in the background; watch the tray.'
              : open
                ? 'Apply runs the recorded fix, dry-run shown first. Dismiss keeps the evidence and asks why.'
                : 'Closed findings keep their evidence.'}
          </p>
          <span className="flex gap-2">
            {mode === 'view' && (
              <>
                <Button variant="ghost" onClick={onClose}>
                  Close
                </Button>
                {open && can('drift.resolve') && detail && (
                  <>
                    <Button variant="secondary" onClick={() => setMode('dismiss')}>
                      Dismiss…
                    </Button>
                    {detail.fix.fixable && (
                      <Button
                        variant="primary"
                        loading={dryRun.isPending}
                        onClick={() => dryRun.mutate()}
                      >
                        Preview the fix
                      </Button>
                    )}
                  </>
                )}
              </>
            )}
            {mode === 'dismiss' && (
              <>
                <Button variant="ghost" onClick={() => setMode('view')}>
                  Back
                </Button>
                <Button
                  variant="secondary"
                  disabled={reason.trim().length === 0}
                  onClick={startDismiss}
                >
                  Dismiss the finding
                </Button>
              </>
            )}
            {mode === 'confirm' && (
              <>
                <Button variant="ghost" onClick={() => setMode('view')}>
                  Back
                </Button>
                <Button variant="primary" onClick={startApply}>
                  Apply
                </Button>
              </>
            )}
          </span>
        </footer>
      </div>
    </div>
  );
}
